import { Box, Checkbox, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { Fragment, useEffect, useMemo, useState } from 'react'

import { useBookingContext } from '@providers/booking/booking_context'
import { getAbsolutePetIndex } from '@providers/booking/helper'
import { TBookingPet } from '@providers/booking/types'
import { formatNumberAsPrice } from '@shared/common'
import { i18WithDefault as t } from '@shared/locale'
import { addonGroupsByCategory } from '@shared/pet_addons'
import { DiscountTag } from 'components-ui/src/discount_tag/discount_tag'
import { isHiddenForSearchGroups } from 'components-ui/src/offer_card/helper'
import {
  AvailabilityGroupClientDto,
  BookingSearchAdminDto,
  BookingSearchByDaysClientDto,
} from 'service-api/src/types/api'

export const PetCard = ({
  petIdx,
  pet,
  groupIdx,
  groom,
  togglePetInGroom,
  addons,
  searchGroupIdx,
}: {
  petIdx: number
  pet: TBookingPet
  groupIdx: number
  groom: BookingSearchByDaysClientDto
  togglePetInGroom: (petIdx: number) => void
  addons: BookingSearchByDaysClientDto[]
  searchGroupIdx: number
}) => {
  const bookingCtx = useBookingContext()
  const theme = useTheme()
  const [isSelected, setIsSelected] = useState<boolean>(true)

  useEffect(() => {
    const currentPetGroom = bookingCtx.state.cart.groups[groupIdx].grooms[petIdx]?.offers.filter(
      (g) => g.offer.id === groom.offer?.id
    )
    setIsSelected(!!currentPetGroom?.[0]?.offer.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.cart.groups[groupIdx].grooms[petIdx], groupIdx])

  const addonGroups = useMemo(() => addonGroupsByCategory(addons), [addons])

  const toggleAddon = (addon: BookingSearchAdminDto) => {
    bookingCtx.dispatch({
      type: 'TOGGLE_GROOM_ADDON',
      payload: { value: { ...addon, pet }, groupIdx, petIdx, searchGroupIdx, groomOfferId: groom.offer?.id },
    })
  }

  const mainPetIndex = getAbsolutePetIndex(bookingCtx.state.cart.groups, groupIdx, petIdx)
  return (
    <Box
      sx={{
        px: 2,
        py: 0.5,
        margin: '0 auto',
        width: '100%',
        backgroundColor: theme.palette!.primary?.shade5,
      }}
    >
      <Stack key={`group-${pet.id}`} direction="row" alignItems="center" spacing={3}>
        <Typography variant="body2" flexGrow={1}>
          {pet.name}
        </Typography>
        {groom.availabilityGroupsByDays?.[0]?.[mainPetIndex]?.availabilities?.[0].price?.rate && (
          <>
            {formatNumberAsPrice(groom.availabilityGroupsByDays?.[0]?.[mainPetIndex]?.availabilities?.[0].price?.rate)}
          </>
        )}
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Tooltip
            title={isSelected ? t('addonItemPicker.removeItem') : t('addonItemPicker.addItem')}
            disableInteractive
          >
            <IconButton
              onClick={() => {
                togglePetInGroom(petIdx)
              }}
              sx={{ p: 0 }}
            >
              <Checkbox sx={{ py: 0.5, ml: 0 }} data-testid="selected-check" checked={isSelected} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {addonGroups &&
        addonGroups.order.map((og) => {
          // Skip groom add-ons hidden by Hatch for this pet (this pet's entry is at
          // day group 0, index searchGroupIdx). Skip the category when none remain.
          const addOnsToRender = addonGroups.items[og].filter(
            (addon: BookingSearchByDaysClientDto) => !isHiddenForSearchGroups(addon, [searchGroupIdx])
          )
          if (!addOnsToRender.length) {
            return null
          }
          return (
            <Fragment key={addOnsToRender[0].offer?.id}>
              <Typography marginX={2} variant="body2" color={theme.palette.text.secondary}>
                {og}
              </Typography>
              {addOnsToRender.map((addon: BookingSearchByDaysClientDto) => {
                const currentPetAddons = bookingCtx.state.cart.groups[groupIdx].groomAddOn?.[petIdx] || []
                // Scope selection to this groom offer so the same add-on selected under a
                // different groom offer for the same pet doesn't appear checked here.
                const isAddonSelected: boolean =
                  currentPetAddons.some((g) => g.offer.id === addon.offer?.id && g.groomOfferId === groom.offer?.id) ||
                  false
                let availabilityGroups = [] as AvailabilityGroupClientDto[]
                if ('availabilityGroups' in addon) {
                  availabilityGroups = addon.availabilityGroups as AvailabilityGroupClientDto[]
                }
                return (
                  <Stack key={addon.offer?.id} direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <IconButton
                      onClick={() => {
                        toggleAddon(addon)
                      }}
                      sx={{ p: '6px' }}
                    >
                      <Checkbox
                        sx={{ py: 0.5, ml: 0 }}
                        data-testid="selected-check"
                        checked={isAddonSelected}
                        disabled={
                          !addon.availabilityGroupsByDays?.[0]?.some((ag) =>
                            ag.availabilities?.some((a) => a.available)
                          )
                        }
                      />
                    </IconButton>
                    <Typography component="span" sx={{ minWidth: 0 }}>
                      {addon.offer?.displayName}
                      {' ('}
                      {addon.availabilityGroupsByDays?.[0]?.[searchGroupIdx].availabilities?.[0].price?.rate && (
                        <>
                          {addon.offer?.priceVaries && `${t('offers.startingAt')}${' '}`}
                          {formatNumberAsPrice(
                            addon.availabilityGroupsByDays?.[0]?.[searchGroupIdx].availabilities?.[0].price?.rate
                          )}
                        </>
                      )}
                      {')'}
                    </Typography>
                    <DiscountTag
                      discounts={availabilityGroups[searchGroupIdx]?.availabilities?.[0]?.price?.discounts}
                      cssOverrides={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        color: 'text.primary',
                        '& .MuiChip-label': {
                          color: 'inherit',
                        },
                        '& .MuiChip-icon': {
                          fill: 'action.active !important',
                        },
                      }}
                      iconColor="action.active"
                      eligibleForConsumableBenefits={addon.eligibleForConsumableBenefits}
                    />
                  </Stack>
                )
              })}
            </Fragment>
          )
        })}
    </Box>
  )
}
